import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { campaignsApiPath } from './apiPath';

/** 4탭 분류 (PENDING=신청, CONFIRMED=진행, COMPLETED=완료, NOT_SELECTED=미선정) */
export const SeedingRecruitmentStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'COMPLETED',
  'NOT_SELECTED',
]);
export type SeedingRecruitmentStatus = z.infer<typeof SeedingRecruitmentStatusSchema>;

/** raw 참여 상태 (4탭 분류와 별도) — searchStatus 가 그룹핑한 원본 status */
export const SeedingParticipationStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'REJECTED',
  'AUTO_TIMEOUT',
  'NOT_SELECTED',
  'SELF_WITHDRAWN',
  'EXPIRED',
  'COMPLETED',
]);
export type SeedingParticipationStatus = z.infer<typeof SeedingParticipationStatusSchema>;

/** SNS 채널 — spec 기준 (INSTAGRAM/YOUTUBE/TWITTER/TIKTOK/NAVER_BLOG/THREADS/SNAP) */
export const SeedingContentChannelSchema = z.enum([
  'INSTAGRAM',
  'YOUTUBE',
  'TWITTER',
  'TIKTOK',
  'NAVER_BLOG',
  'THREADS',
  'SNAP',
]);
export type SeedingContentChannel = z.infer<typeof SeedingContentChannelSchema>;

/** 배송 상태 — 진행 탭에서만 의미 */
export const SeedingFulfillmentStatusSchema = z.enum([
  'SHIPPING_READY',
  'SHIPPING',
  'DELIVERED',
  'CANCELLED',
]);
export type SeedingFulfillmentStatus = z.infer<typeof SeedingFulfillmentStatusSchema>;

/** 콘텐츠 유형 — 등록 이력 + 신청·수락 시 선택값으로 동일 enum 재사용 */
export const SeedingContentTypeSchema = z.enum([
  'IMAGE',
  'SHORT_FORM',
  'LONG_FORM',
  'TEXT',
  'BLOG_POST',
]);
export type SeedingContentType = z.infer<typeof SeedingContentTypeSchema>;

/** 등록 콘텐츠 이력 — COMPLETED 탭에서만 채워짐 (채널별 1건씩) */
export const RegisteredContentSchema = z
  .object({
    channel: SeedingContentChannelSchema,
    contentType: SeedingContentTypeSchema,
    /** yyyy-MM-dd */
    registeredAt: z.string(),
  })
  .describe('RegisteredContentSchema');
export type RegisteredContent = z.infer<typeof RegisteredContentSchema>;

/** 시딩 상품 슬롯 1건 — 슬롯 내 첫 번째 상품이 대표 (아더컬러 그룹은 같은 슬롯 내 다른 goods) */
const CampaignGoodsItemSchema = z
  .object({
    goodsNo: z.number(),
    goodsName: z.string(),
    imageUrl: z.string(),
    /** 판매가 (무신사: goods.price, 29CM: displayPrice) */
    price: z.number(),
    /** 최종 금액 (세일+쿠폰+추가할인, 무신사 전용) */
    finalPrice: z.number().nullable().optional(),
  })
  .describe('CampaignGoodsItemSchema');
export type CampaignGoodsItem = z.infer<typeof CampaignGoodsItemSchema>;

export const CampaignGoodsSlotSchema = z
  .object({
    slotNo: z.number(),
    /** 슬롯 내 상품 목록 (대표는 첫 번째). FE 카드 "외 N개" 표기 = goodsSlots.length - 1 */
    goods: z.array(CampaignGoodsItemSchema),
  })
  .describe('CampaignGoodsSlotSchema');
export type CampaignGoodsSlot = z.infer<typeof CampaignGoodsSlotSchema>;

const GetSeedingMeRecruitmentRequestSchema = z
  .object({
    searchStatus: SeedingRecruitmentStatusSchema,
    page: z.number().min(1).optional(),
    size: z.number().min(1).max(100).optional(),
  })
  .describe('GetSeedingMeRecruitmentRequestSchema');

export type GetSeedingMeRecruitmentRequest = z.infer<typeof GetSeedingMeRecruitmentRequestSchema>;

const SeedingRecruitmentItemSchema = z
  .object({
    /** 큐레이터 참여 ID (상세 진입용) */
    participationId: z.number(),
    campaignId: z.number(),
    /** 4탭 분류 */
    searchStatus: SeedingRecruitmentStatusSchema,
    /** raw 참여 상태 (4탭 분류와 별도 — sub-status 판정용) */
    participationStatus: SeedingParticipationStatusSchema,
    goodsImage: z.string(),
    /** 브랜드 코드(이벤트 로그 brand_id) */
    brandId: z.string(),
    brandName: z.string(),
    /** 캠페인명(이벤트 로그 campaign_title) */
    title: z.string(),
    /** 상품명 */
    goodsName: z.string(),
    /** 상품 가격 (옵션 다수일 경우 최고가) */
    goodsPrice: z.number(),
    snsChannels: z.array(SeedingContentChannelSchema),
    /** 타이머 종료 시점 — 신청=선정발표일, 진행(배송완료)=콘텐츠 제출 마감일 */
    timerEndsAt: z.string().nullable(),
    /** 배송 상태 (진행 탭에서만 의미) */
    fulfillmentStatus: SeedingFulfillmentStatusSchema.nullable(),
    /** 선정 발표일 */
    announcementAt: z.string().nullable(),
    /** 신청 시 큐레이터가 브랜드에 전달한 메시지 */
    messageToBrand: z.string().nullable(),
    /** 신청일 */
    createdAt: z.string(),
    /** 콘텐츠 등록 이력 — COMPLETED 탭에서만 채워짐, 그 외 탭은 빈 배열 */
    registeredContents: z.array(RegisteredContentSchema),
    /** 캠페인의 시딩 상품 슬롯 목록 — 모든 탭에서 항상 채움. "외 N개" = goodsSlots.length - 1 */
    goodsSlots: z.array(CampaignGoodsSlotSchema),
  })
  .describe('SeedingRecruitmentItemSchema');

/** spec PageableResponseGetMyRecruitmentCampaignsV2Response 의 inner. 사내 표준 wrapper(data/meta) + link 는 별도. */
const GetSeedingMeRecruitmentResponseDataSchema = z.object({
  list: z.array(SeedingRecruitmentItemSchema),
});

/** 응답 envelope의 link — spec 누락이라 클라이언트에서 명시. next=다음 페이지 URL(없으면 null), total=총 건수 */
const PageLinkSchema = z.object({
  next: z.string().nullable(),
  total: z.number(),
});

const GetSeedingMeRecruitmentResponseSchema = createApiResponseSchema(
  GetSeedingMeRecruitmentResponseDataSchema,
).extend({
  link: PageLinkSchema.optional(),
});

export type GetSeedingMeRecruitmentResponse = z.infer<typeof GetSeedingMeRecruitmentResponseSchema>;

export const getSeedingMeRecruitment = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetSeedingMeRecruitmentRequestSchema),
    async (params: GetSeedingMeRecruitmentRequest): Promise<GetSeedingMeRecruitmentResponse> =>
      executeStandardRequest(async () => {
        const response = await privateHttpClient.get<GetSeedingMeRecruitmentResponse>(
          urlJoin(campaignsApiPath.getSeedingMeRecruitment),
          params,
        );
        return response;
      }),
    validateApiResponse(GetSeedingMeRecruitmentResponseSchema),
  ),
);
